import { listAgentRunsAppAgentRunsGetOptions } from '@/client/@tanstack/react-query.gen'
import { Card, CardContent } from '@/components/ui/card'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ExpandPanel } from './-components/expand-panel'
import { HeaderStats } from './-components/header-stats'
import { KanbanBoard, KanbanBoardSkeleton } from './-components/kanban-board'
import { LaunchAgentDialog } from './-components/launch-agent-dialog'
import './-components/kanban.css'

interface AgentRunsSearch {
  run?: number
}

export const Route = createFileRoute('/_authed/_user/agent-runs/')({
  validateSearch: (raw: Record<string, unknown>): AgentRunsSearch => {
    const RUN_KEY = 'run'
    const run = raw[RUN_KEY]
    if (typeof run === 'number' && Number.isFinite(run) && run > 0)
      return { run }
    if (typeof run === 'string') {
      const n = Number(run)
      if (Number.isFinite(n) && n > 0)
        return { run: n }
    }
    return {}
  },
  beforeLoad: ({ context }) => {
    if (!context.auth.canUseAgentRuns) {
      throw redirect({ to: '/' })
    }
    return { getTitle: () => 'Agent Runs' }
  },
  component: AgentRunsListPage,
})

function AgentRunsListPage() {
  const { run: selectedId } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const handleSelect = (id: number) => {
    void navigate({
      search: prev => ({ ...prev, run: prev.run === id ? undefined : id }),
    })
  }

  const handleClose = () => {
    void navigate({ search: () => ({}) })
  }

  const { data, isLoading, isError, error } = useQuery({
    ...listAgentRunsAppAgentRunsGetOptions({ query: { limit: 200 } }),
    refetchInterval: 5000,
  })

  const runs = data ?? []
  const hasSelection = selectedId !== undefined && runs.some(r => r.id === selectedId)

  return (
    <div className="agent-kanban flex min-h-0 flex-1 flex-col px-4 py-4 lg:px-6 md:py-6">
      <div className="kb-header">
        <LaunchAgentDialog />
        <span className="kb-strip-divider" aria-hidden />
        <HeaderStats runs={runs} />
      </div>

      {isLoading && <KanbanBoardSkeleton />}

      {isError && (
        <Card>
          <CardContent className="py-10 text-center text-red-600">
            Failed to load runs:
            {' '}
            {String(error)}
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && (
        <ResizablePanelGroup
          orientation="vertical"
          className="min-h-0 flex-1"
        >
          <ResizablePanel defaultSize={40} minSize={10} className="min-h-0 pb-3">
            <KanbanBoard runs={runs} selectedId={selectedId} onSelect={handleSelect} />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={60} minSize={15} className="min-h-0 pb-2 pt-3">
            {hasSelection && selectedId !== undefined
              ? <ExpandPanel key={selectedId} runId={selectedId} onClose={handleClose} />
              : (
                  <div className="kb-expand-empty">
                    Select a run from the board to see live tool-graph + events here.
                  </div>
                )}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  )
}
